import { useForm } from 'react-hook-form';

import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { MultiSelect } from '@/modules/common/components/molecules/selectors/multiselect';
import { SingleSelect } from '@/modules/common/components/molecules/selectors/single-select/single-select';
import { normalizeAttributeKey } from '@/modules/common/mappers/custom-attribute.mapper';
import { CustomAttributeType, SelectOption } from '@/modules/common/types/custom-attribute';

export interface CustomAttributesFieldsProps {
  customAttributes: Array<{
    id: number;
    name: string;
    type: CustomAttributeType;
    options?: SelectOption[];
  }>;
  form: ReturnType<typeof useForm<any>>;
  fieldPrefix?: string;
  isReadMode: boolean;
  t: (key: string, options?: any) => string;
}

export function CustomAttributesFields({
  customAttributes,
  form,
  fieldPrefix = 'customAttributes',
  isReadMode,
  t,
}: CustomAttributesFieldsProps) {
  if (!customAttributes || customAttributes.length === 0) {
    return null;
  }

  return (
    <>
      {customAttributes.map(attribute => {
        const attributeKey = normalizeAttributeKey(attribute.name);
        const fieldName = `${fieldPrefix}.${attributeKey}`;

        if (attribute.type === CustomAttributeType.MultiSelect && attribute.options?.length) {
          return (
            <FormField
              key={attribute.id}
              control={form.control}
              name={fieldName as any}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{attribute.name}</FormLabel>
                  <MultiSelect
                    options={attribute.options!.map(opt => ({
                      value: String(opt.id),
                      label: opt.value,
                    }))}
                    onValueChange={field.onChange}
                    defaultValue={Array.isArray(field.value) ? field.value : []}
                    placeholder={
                      isReadMode
                        ? t('notSelected')
                        : t('selectPlaceholder', { field: attribute.name.toLowerCase() })
                    }
                    disabled={isReadMode}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          );
        }

        if (attribute.options?.length) {
          return (
            <SingleSelect
              key={attribute.id}
              name={fieldName as any}
              control={form.control}
              options={attribute.options.map(opt => ({
                value: String(opt.id),
                label: opt.value,
              }))}
              label={attribute.name}
              placeholder={
                isReadMode
                  ? t('notSelected')
                  : t('selectPlaceholder', { field: attribute.name.toLowerCase() })
              }
              disabled={isReadMode}
              searchable={attribute.options.length > 5}
              isClearSelectionEnabled={true}
            />
          );
        }

        return (
          <FormField
            key={attribute.id}
            control={form.control}
            name={fieldName as any}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{attribute.name}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? ''}
                    placeholder={t('enterPlaceholder', {
                      field: attribute.name.toLowerCase(),
                    })}
                    disabled={isReadMode}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );
      })}
    </>
  );
}
